/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/

import { 
    ApplicationState,
    ConfigState,
    CountryData,
    LibraryData,
    ProductState,
    StraussLogoType
} from './interfaces';

import * as API from './api';
import { logAddToCart, logOrder, logRemoveFromCart } from '../helpers/piwik';

// Splashscreen
export const UI_GOTO = 'UI_GOTO';
export const UI_SET_CODE = 'UI_SET_CODE';
export const UI_SET_ACTIVE_PRODUCT = 'UI_SET_ACTIVE_PRODUCT';
export const UI_SET_ACTIVE_LOGO_AREA = 'UI_SET_ACTIVE_LOGO_AREA';
export const UI_SET_MOBILE_ORDER_STEP = 'UI_SET_MOBILE_ORDER_STEP';
export const UI_SET_EDIT_SCREEN_TAB = 'UI_SET_EDIT_SCREEN_TAB';
export const UI_SET_VAT = 'UI_SET_VAT';
export const UI_SET_COUNTRY = 'UI_SET_COUNTRY';
export const UI_SHOW_COUNTRY_CHANGE_INFO = 'UI_SHOW_COUNTRY_CHANGE_INFO';
export const UI_SET_POCKET_ATTACHED = 'UI_SET_POCKET_ATTACHED';
export const UI_SET_PREVIEW_ARTICLE_1 = 'UI_SET_PREVIEW_ARTICLE_1';
export const UI_SET_PREVIEW_ARTICLE_2 = 'UI_SET_PREVIEW_ARTICLE_2';
export const UI_SET_DETAIL_ARTICLE_INDEX = 'UI_SET_DETAIL_ARTICLE_INDEX';
export const UI_SET_CUSTOM_BASE_COLOR = 'UI_SET_CUSTOM_BASE_COLOR';
export const UI_SET_CUSTOM_CONTRAST_COLOR = 'UI_SET_CUSTOM_CONTRAST_COLOR';

export function uiGoTo(route: string) {
    return {
        type: UI_GOTO,
        route
    }
}

export function uiSetCode(code: string) {
    return {
        type: UI_SET_CODE,
        code
    }
}

export function uiSetCountry(country: CountryData) {
    return {
        type: UI_SET_COUNTRY,
        country
    }
}

export function uiShowCountryChangeInfo(showCountryChangeInfo: boolean) {
    return {
        type: UI_SHOW_COUNTRY_CHANGE_INFO,
        showCountryChangeInfo
    }
}

export function uiSetActiveProduct(product: number) {
    return {
        type: UI_SET_ACTIVE_PRODUCT,
        activeProduct: product
    }
}

export function uiSetActiveLogoArea(index: number) {
    return {
        type: UI_SET_ACTIVE_LOGO_AREA,
        activeLogoArea: index
    }
}

export function uiSetMobileOrderStep(mobileOrderStep: string) {
    return {
        type: UI_SET_MOBILE_ORDER_STEP,
        mobileOrderStep
    }
}

export function uiSetEditScreenTab(editScreenTab: string) {
    return {
        type: UI_SET_EDIT_SCREEN_TAB,
        editScreenTab
    }
}

export function uiSetVat(vat: boolean) {
    return {
        type: UI_SET_VAT,
        vat
    }
}

export function uiSetPreviewArticle1(article: string) {
    return {
        type: UI_SET_PREVIEW_ARTICLE_1,
        article
    }
}

export function uiSetPreviewArticle2(article: string) {
    return {
        type: UI_SET_PREVIEW_ARTICLE_2,
        article
    }
}

export function uiSetDetailArticleIndex(detailArticleIndex: number) {
    return {
        type: UI_SET_DETAIL_ARTICLE_INDEX,
        detailArticleIndex
    }
}

export function uiSetCustomBaseColor(customBaseColor: string) {
    return {
        type: UI_SET_CUSTOM_BASE_COLOR,
        customBaseColor
    }
}

export function uiSetCustomContrastColor(customContrastColor: string) {
    return {
        type: UI_SET_CUSTOM_CONTRAST_COLOR,
        customContrastColor
    }
}


// ConfigState data.
export const CONFIG_CLEAR = 'CONFIG_CLEAR';
export const CONFIG_SET_CODE = 'CONFIG_SET_CODE';
export const CONFIG_SET_BASECOLOR = 'CONFIG_SET_BASECOLOR';
export const CONFIG_SET_CONTRASTCOLOR = 'CONFIG_SET_CONTRASTCOLOR';
export const CONFIG_SET_STRAUSSLOGO = 'CONFIG_SET_STRAUSSLOGO';
export const CONFIG_SET_LOGOTYPE = 'CONFIG_SET_LOGOTYPE';
export const CONFIG_SET_BASICS = 'CONFIG_SET_BASICS';
export const CONFIG_SET_INSPECT_COLOR = 'CONFIG_SET_INSPECT_COLOR';
export const CONFIG_SET_INSPECT_SIZE = 'CONFIG_SET_INSPECT_SIZE';
export const CONFIG_ADD_PRODUCT = 'CONFIG_ADD_PRODUCT';
export const CONFIG_UPDATE_PRODUCT = 'CONFIG_UPDATE_PRODUCT';
export const CONFIG_REMOVE_PRODUCT = 'CONFIG_REMOVE_PRODUCT';

export const CONFIG_STORE_REQUEST = 'CONFIG_STORE_REQUEST';
export const CONFIG_STORE_RECEIVE = 'CONFIG_STORE_RECEIVE';
export const CONFIG_GET_REQUEST = 'CONFIG_GET_REQUEST';
export const CONFIG_GET_RECEIVE = 'CONFIG_GET_RECEIVE';
export const CONFIG_SEND_REQUEST = 'CONFIG_SEND_REQUEST';
export const CONFIG_SEND_RECEIVE = 'CONFIG_SEND_RECEIVE';

export const CONFIG_DELETE_LOGO_REQUEST = 'CONFIG_DELETE_LOGO_REQUEST';
export const CONFIG_DELETE_LOGO_RECEIVE = 'CONFIG_DELETE_LOGO_RECEIVE';

export const CONFIG_STORE_LOGO_REQUEST = 'CONFIG_STORE_LOGO_REQUEST';
export const CONFIG_STORE_LOGO_RECEIVE = 'CONFIG_STORE_LOGO_RECEIVE';

export function configClear() {
    return {
        type: CONFIG_CLEAR
    }
}

export function configSetCode(code: string) {
    return {
        type: CONFIG_SET_CODE,
        code
    }
}
export function configSetBaseColor(colorId: string) {
    return (dispatch: any) => {
        dispatch( {
            type: CONFIG_SET_BASECOLOR,
            colorId
        });
        dispatch(configStoreFetch());
    }
}
export function configSetContrastColor(colorId: string) {
    return (dispatch: any) => {
        dispatch( {
            type: CONFIG_SET_CONTRASTCOLOR,
            colorId
        });
        dispatch(configStoreFetch());
    }
}
export function configSetStraussLogo(logo: StraussLogoType) {
    return (dispatch: any) => {
        dispatch( {
            type: CONFIG_SET_STRAUSSLOGO,
            logo
        });
        dispatch(configStoreFetch());
    }
}
export function configSetBasics(basics: boolean) {
    return (dispatch: any) => {
        dispatch( {
            type: CONFIG_SET_BASICS,
            basics
        });
        dispatch(configStoreFetch());
    }
}
export function configSetInspectColor(inspectColor: boolean) {
    return (dispatch: any) => {
        dispatch( {
            type: CONFIG_SET_INSPECT_COLOR,
            inspectColor
        });
        dispatch(configStoreFetch());
    }
}
export function configSetInspectSize(inspectSize: boolean) {
    return (dispatch: any) => {
        dispatch( {
            type: CONFIG_SET_INSPECT_SIZE,
            inspectSize
        });
        dispatch(configStoreFetch());
    }
}

export function configAddProduct(name: string, size: string) {
    return (dispatch: any, getState: any) => {
        logAddToCart(getState(), name, size);
        dispatch( {
            type: CONFIG_ADD_PRODUCT,
            name,
            size
        });
        dispatch(configStoreFetch());
    }
}

export function configUpdateProduct(index: number, state: ProductState) {
    return (dispatch: any) => {
        dispatch( {
            type: CONFIG_UPDATE_PRODUCT,
            index,
            state
        });
        dispatch(configStoreFetch());
    }
}

export function configRemoveProduct(index: number) {
    return (dispatch: any, getState: any) => {
        logRemoveFromCart(getState(), index);
        dispatch( {
            type: CONFIG_REMOVE_PRODUCT,
            index
        });
        dispatch(configStoreFetch());
    }
}

function configStoreRequest() {
    return {
        type: CONFIG_STORE_REQUEST
    }
}

export function configStoreFetch() : any {
    return (dispatch: any, getState: any) => {
        dispatch(configStoreRequest())
        let state: ApplicationState = getState();
        let code = state.config.code === '' ? '-1' : state.config.code; 
        let data = JSON.stringify(state.config);
        return API.fetchAPI(dispatch, getState, API.getURL(API.POST_REQUEST, {id: code}), data, 'POST')
            .then(response => response.json())
            .then(json => dispatch(configStoreReceive(json)))
            .catch(error =>  {
                dispatch(configStoreReceive({success: false, error: error.message}));
            });
    };
}

function configStoreReceived(fetchError: string|null, code: string|null) {
    return {
        type: CONFIG_STORE_RECEIVE,
        fetchError,
        code
    };
}

function configStoreReceive(json: any) {
    return (dispatch: any, getState: any) => {
        let fetchError: string | null = null;
        let code = null;
        if(json) {
            if(json.statusCode !== 200) {
                fetchError = json.error;
            } else {
                code = json.data.code;
            }
        }
        dispatch(configStoreReceived(fetchError, code));
    }
}

function configGetRequest() {
    return {
        type: CONFIG_GET_REQUEST
    }
}

export function configGetFetch() : any {
    return (dispatch: any, getState: any) => {
        dispatch(configGetRequest())
        let state: ApplicationState = getState();
        // Note(daniel): Could we really not escape the last - in this regex?
        // eslint-disable-next-line no-useless-escape
        let sanitizedCode = state.ui.code.replace(/[^a-zA-Z0-9\-]+/g, '');
        let code = sanitizedCode === '' ? '-1' : sanitizedCode; 
        return API.fetchAPI(dispatch, getState, API.getURL(API.GET_REQUEST, {id: code}), state.config, 'GET')
            .then(response => response.json())
            .then(json => dispatch(configGetReceive(json)))
            .catch(error =>  {
                dispatch(configGetReceive({success: false, error: error.message}));
            });
    };
}

function configGetReceived(fetchError: string|null, config: ConfigState) {
    return {
        type: CONFIG_GET_RECEIVE,
        fetchError,
        config
    };
}

function configGetReceive(json: any) {
    return (dispatch: any, getState: any) => {
        let fetchError: string | null = null;
        
        if(json) {
            if(json.statusCode !== 200) {
                fetchError = json.error;
            }
        }

        dispatch(configGetReceived(fetchError, json.data));
    }
}


function configSendRequest() {
    return {
        type: CONFIG_SEND_REQUEST
    }
}

export function configSendFetch(lang: string) : any {
    return (dispatch: any, getState: any) => {
        dispatch(configSendRequest())
        let state: ApplicationState = getState();
        let code = state.config.code === '' ? '-1' : state.config.code; 
        let country = state.ui.country.id;
        let data = JSON.stringify({lang, country, contact: state.contact});
        return API.fetchAPI(dispatch, getState, API.getURL(API.SEND_REQUEST, {id: code}), data, 'POST')
            .then(response => response.json())
            .then(json => dispatch(configSendReceive(json)))
            .catch(error =>  {
                dispatch(configSendReceive({success: false, error: error.message}));
            });
    };
}

function configSendReceived(fetchError: string|null, config: ConfigState) {
    return {
        type: CONFIG_SEND_RECEIVE,
        fetchError,
        config
    };
}

function configSendReceive(json: any) {
    return (dispatch: any, getState: any) => {
        let fetchError: string | null = null;
        if(json) {
            if(json.statusCode !== 200) {
                fetchError = json.error;
            } else {
                if(json.data.success) {
                    let state: ApplicationState = getState();
                    console.log("order sent!");
                    logOrder(state);
                } else {
                    fetchError = json.error;
                }
            }
        }

        dispatch(configSendReceived(fetchError, json.data));
    }
}



function configStoreLogoRequest() {
    return {
        type: CONFIG_STORE_LOGO_REQUEST
    }
}

export function configStoreLogoFetch(data: any) : any {
    return (dispatch: any, getState: any) => {
        dispatch(configStoreLogoRequest())
        let state: ApplicationState = getState();
        let code = state.config.code === '' ? '-1' : state.config.code; 

        return API.fetchAPI(dispatch, getState, API.getURL(API.POST_LOGO, {id: code}), data, 'POST')
            .then(response => response.json())
            .then(json => dispatch(configStoreLogoReceive(json)))
            .catch(error =>  {
                dispatch(configStoreLogoReceive({success: false, error: error.message}));
            });
    };
}

function configStoreLogoReceived(fetchError: string|null, config: ConfigState) {
    return {
        type: CONFIG_STORE_LOGO_RECEIVE,
        fetchError,
        config
    };
}

function configStoreLogoReceive(json: any) {
    return (dispatch: any, getState: any) => {
        let fetchError: string | null = null;
        if(json) {
            if(json.statusCode !== 200) {
                fetchError = json.error;
            }
        }
        dispatch(configStoreLogoReceived(fetchError, json.data));
    }
}

function configDeleteLogoRequest() {
    return {
        type: CONFIG_DELETE_LOGO_REQUEST
    }
}

export function configDeleteLogoFetch(filename: string) : any {
    return (dispatch: any, getState: any) => {
        dispatch(configDeleteLogoRequest())
        let state: ApplicationState = getState();
        let code = state.config.code === '' ? '-1' : state.config.code; 

        return API.fetchAPI(dispatch, getState, API.getURL(API.DELETE_LOGO, {id: code, filename}), {}, 'DELETE')
            .then(response => response.json())
            .then(json => dispatch(configDeleteLogoReceive(json)))
            .catch(error =>  {
                dispatch(configDeleteLogoReceive({success: false, error: error.message}));
            });
    };
}

function configDeleteLogoReceived(fetchError: string|null, config: ConfigState) {
    return {
        type: CONFIG_DELETE_LOGO_RECEIVE,
        fetchError,
        config
    };
}

function configDeleteLogoReceive(json: any) {
    return (dispatch: any, getState: any) => {
        let fetchError: string | null = null;
        if(json) {
            if(json.statusCode !== 200) {
                fetchError = json.error;
            }
        }
        dispatch(configDeleteLogoReceived(fetchError, json.data));
    }
}

// Contact data.
export const CONTACT_SET_ORDERFOR = 'CONTACT_SET_ORDERFOR';
export const CONTACT_SET_CUSTOMERNUMBER = 'CONTACT_SET_CUSTOMERNUMBER';
export const CONTACT_SET_FIRSTNAME = 'CONTACT_SET_FIRSTNAME';
export const CONTACT_SET_LASTNAME = 'CONTACT_SET_LASTNAME';
export const CONTACT_SET_COUNTRY = 'CONTACT_SET_COUNTRY';
export const CONTACT_SET_ZIPCODE = 'CONTACT_SET_ZIPCODE';
export const CONTACT_SET_CITY = 'CONTACT_SET_CITY';
export const CONTACT_SET_ADDRESS = 'CONTACT_SET_ADDRESS';
export const CONTACT_SET_TITLE = 'CONTACT_SET_TITLE';
export const CONTACT_SET_OCCUPATION = 'CONTACT_SET_OCCUPATION';
export const CONTACT_SET_EMAIL = 'CONTACT_SET_EMAIL';
export const CONTACT_SET_PHONE = 'CONTACT_SET_PHONE';
export const CONTACT_SET_COMMENT = 'CONTACT_SET_COMMENT';


export function configSetOrderFor(orderFor: string) {
    return {
        type: CONTACT_SET_ORDERFOR,
        orderFor
    }
}

export function configSetCustomerNumber(customerNumber: string) {
    return {
        type: CONTACT_SET_CUSTOMERNUMBER,
        customerNumber
    }
}

export function configSetFirstName(firstName: string) {
    return {
        type: CONTACT_SET_FIRSTNAME,
        firstName
    }
}

export function configSetLastName(lastName: string) {
    return {
        type: CONTACT_SET_LASTNAME,
        lastName
    }
}

export function configSetCountry(country: string) {
    return {
        type: CONTACT_SET_COUNTRY,
        country
    }
}

export function configSetZipCode(zipCode: string) {
    return {
        type: CONTACT_SET_ZIPCODE,
        zipCode
    }
}

export function configSetCity(city: string) {
    return {
        type: CONTACT_SET_CITY,
        city
    }
}

export function configSetAddress(address: string) {
    return {
        type: CONTACT_SET_ADDRESS,
        address
    }
}
export function configSetTitle(title: string) {
    return {
        type: CONTACT_SET_TITLE,
        title
    }
}

export function configSetOccupation(occupation: string) {
    return {
        type: CONTACT_SET_OCCUPATION,
        occupation
    }
}

export function configSetEmail(email: string) {
    return {
        type: CONTACT_SET_EMAIL,
        email
    }
}

export function configSetPhone(phone: string) {
    return {
        type: CONTACT_SET_PHONE,
        phone
    }
}

export function configSetComment(comment: string) {
    return {
        type: CONTACT_SET_COMMENT,
        comment
    }
}



// Contact data.
export const LIBRARY_GET_RECEIVE = 'LIBRARY_GET_RECEIVE';
export const LIBRARY_GET_REQUEST = 'LIBRARY_GET_REQUEST';


function libraryGetRequest() {
    return {
        type: LIBRARY_GET_REQUEST
    }
}

export function libraryGetFetch() : any {
    return (dispatch: any, getState: any) => {
        dispatch(libraryGetRequest())
        return API.fetchAPI(dispatch, getState, API.GET_LIBRARY, {}, 'GET', 'no-store')
            .then(response => response.json())
            .then(json => dispatch(libraryGetReceive(json)))
            .catch(error =>  {
                dispatch(libraryGetReceive({success: false, error: error.message}));
            });
    };
}

function libraryGetReceived(fetchError: string|null, library: LibraryData, customBaseColor: string|null, customContrastColor: string|null) {
    if(library !== undefined) {
        if(customBaseColor !== null) {
            library.baseColors.push({
                id: 'customBase',
                bgColor: customBaseColor,
                color: customBaseColor,
                name: {
                    de: 'Eigene Basisfarbe',
                    en: 'Custom base color',
                    fr: 'Couleur de base personnalisée'
                }
            })
        }
        if(customContrastColor !== null) {
            library.contrastColors.push({
                id: 'customContrast',
                bgColor: customContrastColor,
                color: customContrastColor,
                name: {
                    de: 'Eigene Kontrastfarbe',
                    en: 'Custom contrast color',
                    fr: 'Couleur de contraste personnalisée'
                }
            })
        }
    }

    return {
        type: LIBRARY_GET_RECEIVE,
        fetchError,
        library
    };
}

function libraryGetReceive(json: any) {
    return (dispatch: any, getState: any) => {
        let fetchError: string | null = null;
        let state = getState();
        if(json) {
            if(json.statusCode !== 200) {
                fetchError = json.error;
            }
        }

        dispatch(libraryGetReceived(fetchError, json.data.data, state.ui.customBaseColor, state.ui.customContrastColor));
    }
}
